import {
  lazy,
  memo,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type LazyExoticComponent,
  type FC,
} from "react";
import { Button, Dropdown, Form, Modal } from "react-bootstrap";
import * as signalR from "@microsoft/signalr";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { FaCheck, FaSearch, FaVideo } from "react-icons/fa";
import { BsThreeDotsVertical } from "react-icons/bs";
import { useTranslation } from "react-i18next";
import { useGetConnections } from "../../api/queries/useGetConnections";
import { useGetMessages } from "../../api/queries/useGetMessages";
import { useTranslateDynamicFields } from "../../api/queries/useTranslateDynamicFields";
import { useAppContext } from "../../Context/AppContext";
import { useExplorerMessagingLimit } from "../../hooks/useExplorerMessagingLimit";
import { useExplorerMessagingLimitFeedback } from "../../hooks/useExplorerMessagingLimitFeedback";
import { ExplorerMessagingLimitCounter } from "./ExplorerMessagingLimitCounter";
import {
  CONNECTION_REMOVED_EVENT,
} from "../../utils/explorerMessagingError";
import { Message, User, UserImageUrls } from "../../types/Types";
import { getAccessToken as readAccessToken } from "../../auth/authStorage";
import { getChatHubUrl } from "../../api/axiosInstance";
import {
  applyOutboundStatusPatch,
  inferOutboundStatusFromMessages,
  loadPersistedOutboundStatus,
  markMessagesSeenUpTo,
  mergeOutboundStatusMaps,
  outboundStatusKey,
  savePersistedOutboundStatus,
  type OutboundMessageStatus,
} from "../../utils/messageDeliveryStatus";
import {
  getLastIncomingMessageDate,
  markConversationAsRead,
} from "../../utils/headerInboxState";

const ChatWindow = lazy(() => import("./ChatWindow"));

type MessageSidebarWrappedProps = {
  conversations: Conversation[];
  selected: Conversation | null;
  onSelect: (conv: Conversation) => void;
  typingUsers: { [key: number]: boolean };
  onlineUserObjectIds: Set<string>;
  userImageUrls?: UserImageUrls[];
  currentUserId?: number;
  translationEnabled?: boolean;
  targetLanguage?: string;
};

const MessageSidebar = lazy(() =>
  import("./MessageSidebar")
) as LazyExoticComponent<FC<MessageSidebarWrappedProps>>;

const SIDEBAR_FALLBACK_MIN_HEIGHT = 300;
const CHAT_FALLBACK_MIN_HEIGHT = 400;
const BACK_ICON_SIZE = 20;

const MAIN_CONTAINER_STYLE: React.CSSProperties = {
  height: "calc(100vh - 140px)",
  minHeight: "500px",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
};
const ROW_STYLE: React.CSSProperties = { overflow: "visible", minHeight: 0 };
const SIDEBAR_COLUMN_STYLE: React.CSSProperties = {
  backgroundColor: "#f8f9fa",
  overflow: "hidden",
  minHeight: 0,
};
const SIDEBAR_FALLBACK_STYLE: React.CSSProperties = {
  minHeight: SIDEBAR_FALLBACK_MIN_HEIGHT,
};
const CHAT_COLUMN_STYLE: React.CSSProperties = {
  overflow: "hidden",
  minHeight: 0,
};
const CHAT_FALLBACK_STYLE: React.CSSProperties = {
  minHeight: CHAT_FALLBACK_MIN_HEIGHT,
};
const MODAL_HEADER_SPAN_STYLE: React.CSSProperties = { cursor: "pointer" };
const TRANSLATION_ENABLED_STORAGE_KEY = "messages.translationEnabled";

const AVAILABLE_LANGUAGES = [
  { code: "en", label: "English" },
  { code: "fr", label: "French" },
  { code: "nl", label: "Dutch" },
  { code: "it", label: "Italian" },
  { code: "es", label: "Spanish" },
  { code: "ar", label: "Standard Arabic" },
  { code: "zh-CN", label: "Mandarin Chinese" },
  { code: "pt", label: "Portuguese" },
] as const;
type LanguageCode = (typeof AVAILABLE_LANGUAGES)[number]["code"];

export interface Conversation {
  messages: Message[];
  id: number;
  objectId: string;
  firstName: string;
  lastName: string;
  email: string;
  lastMessageDate?: number;
  /** Spread from connection user row when building conversations */
  hasPaid?: number;
  isDirectory?: number;
}

function MessagesInner() {
  const [mobileChatOpen, setMobileChatOpen] = useState(false);
  const [selected, setSelected] = useState<Conversation | null>(null);
  const [typingUsers, setTypingUsers] = useState<{ [key: number]: boolean }>(
    {}
  );
  const [onlineUserObjectIds, setOnlineUserObjectIds] = useState<Set<string>>(
    () => new Set()
  );
  const [outboundMessageStatus, setOutboundMessageStatusState] = useState<
    Record<string, OutboundMessageStatus>
  >(() => loadPersistedOutboundStatus(localStorage.getItem("objectId") || ""));
  const [hubConnection, setHubConnection] =
    useState<signalR.HubConnection | null>(null);
  const [translationEnabled, setTranslationEnabled] = useState<boolean>(() => {
    const stored = localStorage.getItem(TRANSLATION_ENABLED_STORAGE_KEY);
    return stored === "true";
  });
  const [targetLanguage, setTargetLanguage] = useState<LanguageCode>("en");
  const [searchText, setSearchText] = useState("");
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const { id: messageIdParam } = useParams();
  const messageId = messageIdParam ? parseInt(messageIdParam, 10) : null;
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const guid = localStorage.getItem("objectId");

  const persistOutboundStatus = useCallback(
    (
      updater:
        | Record<string, OutboundMessageStatus>
        | ((
            prev: Record<string, OutboundMessageStatus>
          ) => Record<string, OutboundMessageStatus>)
    ) => {
      setOutboundMessageStatusState((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        const objectId = localStorage.getItem("objectId");
        if (objectId) savePersistedOutboundStatus(objectId, next);
        return next;
      });
    },
    []
  );

  useEffect(() => {
    if (!guid) {
      setOutboundMessageStatusState({});
      return;
    }
    setOutboundMessageStatusState(loadPersistedOutboundStatus(guid));
  }, [guid]);

  const connectionRef = useRef<signalR.HubConnection | null>(null);

  const { data: userMessages } = useGetMessages({ id: guid || "" });
  const { data: userResponse, refetch: refetchConnections } = useGetConnections({
    userId: guid || "",
  });
  const userResponseRef = useRef(userResponse);
  userResponseRef.current = userResponse;

  const { isExplorer } = useExplorerMessagingLimit();
  const {
    canSendMessage: explorerCanSendMessage,
    handleMessagingLimitError,
    openPaywall: openMessagingPaywall,
    limitModal: explorerMessagingLimitModal,
    refetchLimit: refetchExplorerMessagingLimit,
  } = useExplorerMessagingLimitFeedback({ autoShowPaywall: true });

  const { user } = useAppContext();

  const userId = user?.user?.id;

  useEffect(() => {
    if (!guid || userId == null || !userMessages?.length) return;
    const allMessages = userMessages.flatMap((item) => item.messages ?? []);
    const inferred = inferOutboundStatusFromMessages(allMessages, userId, guid);
    setOutboundMessageStatusState((prev) => {
      const next = mergeOutboundStatusMaps(prev, inferred);
      savePersistedOutboundStatus(guid, next);
      return next;
    });
  }, [guid, userId, userMessages]);

  const users = useMemo(() => {
    return userResponse?.userDtos.filter(
      (user, index, self) =>
        index === self.findIndex((u) => u.objectId === user.objectId) &&
        user.id !== userId
    );
  }, [userResponse, userId]);

  const connectedUserIds = useMemo(
    () => new Set((users ?? []).map((u) => u.id)),
    [users]
  );

  const selectedConnectionAllowed = useMemo(() => {
    if (!selected) return false;
    if (!isExplorer) return true;
    return connectedUserIds.has(selected.id);
  }, [selected, isExplorer, connectedUserIds]);

  const composeEnabled = useMemo(() => {
    if (!selectedConnectionAllowed) return false;
    if (!isExplorer) return true;
    return explorerCanSendMessage;
  }, [selectedConnectionAllowed, isExplorer, explorerCanSendMessage]);

  const explorerMessagingLimitReached = useMemo(
    () => isExplorer && selectedConnectionAllowed && !explorerCanSendMessage,
    [isExplorer, selectedConnectionAllowed, explorerCanSendMessage]
  );

  const conversations = useMemo(() => {
    if (!users || !userMessages) return [];

    const list = users
      .map((user) => {
        const messages = userMessages.flatMap((item) =>
          item.messages.filter(
            (msg) =>
              Number(msg.userIdMessageFrom) === Number(user.id) ||
              Number(msg.userIdMessageTo) === Number(user.id)
          )
        );

        messages.sort(
          (a, b) =>
            new Date(a.dateSent || "").getTime() -
            new Date(b.dateSent || "").getTime()
        );

        const lastMessageDate = messages[messages.length - 1]?.dateSent
          ? new Date(messages[messages.length - 1].dateSent!).getTime()
          : 0;

        return {
          ...user,
          messages,
          lastMessageDate,
        };
      })
      .sort((a, b) => b.lastMessageDate - a.lastMessageDate);

    if (!isExplorer) return list;
    return list.filter((c) => connectedUserIds.has(c.id));
  }, [users, userMessages, isExplorer, connectedUserIds]);

  const filteredConversations = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter((c) =>
      `${c.firstName} ${c.lastName}`.toLowerCase().includes(q)
    );
  }, [conversations, searchText]);

  const updateMessageCache = useCallback(
    (newMessage: Message) => {
      if (!guid) return;

      const isFromSelf =
        userId != null &&
        Number(newMessage.userIdMessageFrom) === Number(userId);
      if (isFromSelf) {
        persistOutboundStatus((prev) =>
          applyOutboundStatusPatch(prev, {
            [outboundStatusKey(
              newMessage.dateSent,
              newMessage.userIdMessageTo
            )]: "sent",
          })
        );
      }

      queryClient.setQueryData(["getMessages", guid], (oldData: any) => {
        if (!oldData?.length) {
          return [{ messages: [newMessage] }];
        }

        const isDuplicate = oldData.some((item: any) =>
          item.messages?.some(
            (msg: Message) =>
              msg.body === newMessage.body &&
              msg.dateSent === newMessage.dateSent &&
              Number(msg.userIdMessageFrom) === Number(newMessage.userIdMessageFrom) &&
              Number(msg.userIdMessageTo) === Number(newMessage.userIdMessageTo)
          )
        );
        if (isDuplicate) return oldData;

        const userPairExists = oldData.some((item: any) =>
          item.messages?.some(
            (msg: Message) =>
              (Number(msg.userIdMessageFrom) === Number(newMessage.userIdMessageFrom) &&
                Number(msg.userIdMessageTo) === Number(newMessage.userIdMessageTo)) ||
              (Number(msg.userIdMessageFrom) === Number(newMessage.userIdMessageTo) &&
                Number(msg.userIdMessageTo) === Number(newMessage.userIdMessageFrom))
          )
        );

        if (!userPairExists) {
          return [...oldData, { messages: [newMessage] }];
        }

        return oldData.map((item: any) => {
          const isSameConversation = item.messages?.some(
            (msg: Message) =>
              (Number(msg.userIdMessageFrom) === Number(newMessage.userIdMessageFrom) &&
                Number(msg.userIdMessageTo) === Number(newMessage.userIdMessageTo)) ||
              (Number(msg.userIdMessageFrom) === Number(newMessage.userIdMessageTo) &&
                Number(msg.userIdMessageTo) === Number(newMessage.userIdMessageFrom))
          );

          if (isSameConversation) {
            return {
              ...item,
              messages: [...item.messages, newMessage],
            };
          }
          return item;
        });
      });
    },
    [guid, queryClient, userId, persistOutboundStatus]
  );

  const seedOnlineUsers = useCallback(
    async (connection: signalR.HubConnection) => {
      const peers =
        userResponseRef.current?.userDtos
          .map((u) => u.objectId)
          .filter((id): id is string => !!id && id !== guid) ?? [];
      if (!peers.length) return;

      const online = new Set<string>();
      await Promise.all(
        peers.map(async (objectId) => {
          try {
            const isOnline = await connection.invoke<boolean>(
              "IsUserOnline",
              objectId
            );
            if (isOnline) online.add(objectId);
          } catch {
            /* hub may be unavailable */
          }
        })
      );
      if (online.size) {
        setOnlineUserObjectIds(
          (prev) => new Set([...Array.from(prev), ...Array.from(online)])
        );
      }
    },
    [guid]
  );

  const getAccessToken = useCallback(async (): Promise<string> => {
    const token = readAccessToken();
    if (!token) throw new Error("No access token found");
    return token;
  }, []);

  useEffect(() => {
    if (!guid || connectionRef.current) return;

    let cancelled = false;

    const connect = async () => {
      try {
        const token = await getAccessToken();
        if (cancelled) return;

        const newConnection = new signalR.HubConnectionBuilder()
          .withUrl(getChatHubUrl(), {
            accessTokenFactory: async () => token,
          })
          .withAutomaticReconnect()
          .configureLogging(signalR.LogLevel.Warning)
          .build();

        newConnection.on("ReceiveMessage", (payload: any) => {
          const newMsg: Message = {
            id: Math.random(),
            body: payload.body ?? payload.Body ?? "",
            dateSent:
              payload.dateSent ??
              payload.DateSent ??
              new Date().toISOString(),
            userIdMessageFrom: payload.senderId ?? payload.SenderId,
            userIdMessageTo: payload.receiverId ?? payload.ReceiverId,
          };
          updateMessageCache(newMsg);
        });

        newConnection.on("MessageDelivered", (payload: any) => {
          const receiverId = payload.receiverId ?? payload.ReceiverId;
          const dateSent = payload.dateSent ?? payload.DateSent;
          if (!receiverId || !dateSent) return;
          persistOutboundStatus((prev) =>
            applyOutboundStatusPatch(prev, {
              [outboundStatusKey(String(dateSent), Number(receiverId))]:
                "delivered",
            })
          );
        });

        newConnection.on(
          "MessagesSeen",
          (viewerObjectId: string, lastSeenDateSent: string) => {
            const viewer = userResponseRef.current?.userDtos.find(
              (u) => u.objectId === viewerObjectId
            );
            if (!viewer || !lastSeenDateSent) return;

            persistOutboundStatus((prev) => {
              const allMessages =
                (queryClient.getQueryData(["getMessages", guid]) as any[])?.flatMap(
                  (item: { messages?: Message[] }) => item.messages ?? []
                ) ?? [];
              return markMessagesSeenUpTo(
                prev,
                viewer.id,
                allMessages,
                userId,
                guid,
                String(lastSeenDateSent)
              );
            });
          }
        );

        newConnection.on(
          "UserPresenceChanged",
          (objectId: string, isOnline: boolean) => {
            if (!objectId || objectId === guid) return;
            setOnlineUserObjectIds((prev) => {
              const next = new Set(prev);
              if (isOnline) next.add(objectId);
              else next.delete(objectId);
              return next;
            });
          }
        );

        newConnection.on("UserTyping", (senderObjectId: string) => {
          const sender = userResponseRef.current?.userDtos.find(
            (u) => u.objectId === senderObjectId
          );
          if (sender) {
            setTypingUsers((prev) => ({ ...prev, [sender.id]: true }));
          }
        });

        newConnection.on("UserStoppedTyping", (senderObjectId: string) => {
          const sender = userResponseRef.current?.userDtos.find(
            (u) => u.objectId === senderObjectId
          );
          if (sender) {
            setTypingUsers((prev) => ({ ...prev, [sender.id]: false }));
          }
        });

        newConnection.onreconnected(() => {
          void seedOnlineUsers(newConnection);
        });

        await newConnection.start();
        if (cancelled) {
          await newConnection.stop();
          return;
        }
        connectionRef.current = newConnection;
        setHubConnection(newConnection);
        await seedOnlineUsers(newConnection);
      } catch (err) {
        console.error("SignalR connection error:", err);
      }
    };

    connect();

    return () => {
      cancelled = true;
      void connectionRef.current?.stop();
      connectionRef.current = null;
      setHubConnection(null);
    };
  }, [guid, getAccessToken, updateMessageCache, seedOnlineUsers, queryClient, userId, persistOutboundStatus]);

  useEffect(() => {
    if (!hubConnection || !userResponse?.userDtos?.length) return;
    void seedOnlineUsers(hubConnection);
  }, [hubConnection, userResponse?.userDtos, seedOnlineUsers]);

  useEffect(() => {
    if (!messageId || !conversations.length) return;

    const matched = conversations.find((c) => c.id === messageId);
    if (matched) {
      setSelected(matched);
      if (window.innerWidth < 768) setMobileChatOpen(true);
    } else if (isExplorer) {
      navigate("/messages", { replace: true });
    }
  }, [messageId, conversations, isExplorer, navigate]);

  useEffect(() => {
    if (!isExplorer || !selected) return;
    if (!connectedUserIds.has(selected.id)) {
      setSelected(null);
      setMobileChatOpen(false);
      if (messageId === selected.id) {
        navigate("/messages", { replace: true });
      }
    }
  }, [isExplorer, selected, connectedUserIds, messageId, navigate]);

  useEffect(() => {
    if (!isExplorer) return;

    const handleConnectionRemoved = (event: Event) => {
      const detail = (event as CustomEvent<{ userId: number }>).detail;
      void refetchConnections();
      if (selected?.id === detail?.userId) {
        setSelected(null);
        setMobileChatOpen(false);
        navigate("/messages", { replace: true });
      }
    };

    window.addEventListener(CONNECTION_REMOVED_EVENT, handleConnectionRemoved);
    return () =>
      window.removeEventListener(CONNECTION_REMOVED_EVENT, handleConnectionRemoved);
  }, [isExplorer, selected, refetchConnections, navigate]);

  useEffect(() => {
    if (!selected) return;
    const updated = conversations.find((c) => c.id === selected.id);
    if (!updated) return;
    const prevLen = selected.messages?.length ?? 0;
    const nextLen = updated.messages?.length ?? 0;
    if (
      nextLen !== prevLen ||
      selected.lastMessageDate !== updated.lastMessageDate
    ) {
      setSelected(updated);
    }
  }, [conversations, selected]);

  useEffect(() => {
    if (!guid || !selected) return;
    const latestIncomingDate = getLastIncomingMessageDate(selected, userId);
    markConversationAsRead(guid, selected.id, latestIncomingDate);
  }, [guid, selected, userId]);

  useEffect(() => {
    const handleMessageSent = (e: any) => {
      const { newMessage } = e.detail;
      updateMessageCache(newMessage);
    };

    window.addEventListener("messageSent", handleMessageSent);
    return () => window.removeEventListener("messageSent", handleMessageSent);
  }, [updateMessageCache]);

  const handleSelect = useCallback(
    (conversation: Conversation) => {
      if (isExplorer && !connectedUserIds.has(conversation.id)) {
        return;
      }
      setSelected(conversation);
      navigate(`/messages/${conversation.id}`);
      if (window.innerWidth < 768) setMobileChatOpen(true);
    },
    [navigate, isExplorer, connectedUserIds]
  );

  const handleUserClick = useCallback((user: User) => {
    setSelectedUser(user);
    setShowProfileModal(true);
  }, []);

  const handleVideoCallClick = useCallback(() => {
    if (!selected?.objectId) return;
    const videoCallUrl = `/video-call/${selected.objectId}`;
    window.open(videoCallUrl, "_blank", "noopener,noreferrer");
  }, [selected?.objectId]);

  const handleCloseMobileChat = useCallback(() => {
    setMobileChatOpen(false);
    setSelected(null);
    navigate("/messages");
  }, [navigate]);

  const handleModalHeaderNameClick = useCallback(() => {
    const user = userResponse?.userDtos.find((u) => u.id === selected?.id);
    const imageEntry = userResponse?.userImageUrls?.find(
      (img) => img.userId === user?.id
    );
    if (user && handleUserClick && imageEntry) {
      handleUserClick({ ...user, ...imageEntry?.imageUrls });
    }
  }, [userResponse, selected?.id, handleUserClick]);
  const { data: translatedSelectedName } = useTranslateDynamicFields(
    {
      fullName: `${selected?.firstName || ""} ${selected?.lastName || ""}`.trim(),
    },
    i18n.resolvedLanguage || i18n.language
  );

  useEffect(() => {
    localStorage.setItem(
      TRANSLATION_ENABLED_STORAGE_KEY,
      String(translationEnabled)
    );
  }, [translationEnabled]);

  return (
    <main
      className="page-content pb-4"
      aria-label={t("sidebar.messages")}
    >
      <div
        className="mx-auto shadow rounded messages-card"
        style={MAIN_CONTAINER_STYLE}
      >
        <div className="d-flex px-3 px-md-4 py-3 bg-project-gradient text-white align-items-center justify-content-between rounded-top flex-shrink-0">
          <h4 className="mb-0 fw-bold">{t("sidebar.messages")}</h4>
          <Dropdown align="end">
            <Dropdown.Toggle
              id="messages-translation-dropdown"
              as="button"
              className="d-flex align-items-center justify-content-center btn btn-link text-white text-decoration-none p-0 border-0 shadow-none gap-2"
              aria-label={t("connections.moreActions")}
            >
              <BsThreeDotsVertical size={18} />
            </Dropdown.Toggle>
            <Dropdown.Menu className="mt-2 p-0 rounded-2 overflow-hidden">
              <div className="px-3 py-2 border-bottom">
                <div className="d-flex align-items-center justify-content-between">
                  <span className="small text-dark">
                    {t("messages.translationLabel")}
                  </span>
                  <div className="form-check form-switch m-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="messages-translation-switch"
                      checked={translationEnabled}
                      onChange={(e) => setTranslationEnabled(e.target.checked)}
                    />
                  </div>
                </div>
              </div>
              <div className="px-3 py-2 border-bottom d-flex align-items-center justify-content-between">
                <span className="small text-dark">{t("messages.autoDetect")}</span>
                <FaCheck size={10} color="#35c777" />
              </div>
              {AVAILABLE_LANGUAGES.map((language) => (
                <Dropdown.Item
                  key={language.code}
                  onClick={() => {
                    setTargetLanguage(language.code);
                    if (!translationEnabled) setTranslationEnabled(true);
                  }}
                  className="d-flex align-items-center justify-content-between small py-1 px-3"
                >
                  {language.label}
                  {targetLanguage === language.code ? (
                    <FaCheck size={10} color="#35c777" />
                  ) : null}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>

        <ExplorerMessagingLimitCounter className="mx-3 mx-md-4 mt-2" />

        <div className="row flex-grow-1 m-0 g-0 rounded-bottom" style={ROW_STYLE}>
          {conversations.length > 0 ? (
            <>
              <div
                className={`col-12 col-md-4 shadow p-0 d-flex flex-column ${
                  selected && mobileChatOpen ? "d-none d-md-flex" : ""
                }`}
                style={SIDEBAR_COLUMN_STYLE}
              >
                <div className="px-2 px-md-2 pt-2 pb-2 bg-white shadow-sm">
                  <div className="input-group" dir="ltr">
                    <span className="input-group-text bg-white border-end-0">
                      <FaSearch className="text-secondary" size={14} />
                    </span>
                    <Form.Control
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      placeholder={t("messages.searchPlaceholder")}
                      className="border-start-0"
                      dir={i18n.dir()}
                      style={i18n.dir() === "rtl" ? { textAlign: "right" } : undefined}
                    />
                  </div>
                </div>
                <Suspense
                  fallback={
                    <div
                      className="d-flex align-items-center justify-content-center"
                      style={SIDEBAR_FALLBACK_STYLE}
                      aria-hidden
                    >
                      <span className="text-muted">{t("messages.loading")}</span>
                    </div>
                  }
                >
                  <MessageSidebar
                    conversations={filteredConversations}
                    selected={selected}
                    onSelect={handleSelect}
                    typingUsers={typingUsers}
                    onlineUserObjectIds={onlineUserObjectIds}
                    userImageUrls={userResponse?.userImageUrls}
                    currentUserId={userId}
                    translationEnabled={translationEnabled}
                    targetLanguage={targetLanguage}
                  />
                </Suspense>
              </div>

              <div className="col-12 col-md-8 shadow-sm p-0 d-none d-md-flex flex-column" style={CHAT_COLUMN_STYLE}>
                {selected ? (
                  <Suspense
                    fallback={
                      <div
                        className="d-flex align-items-center justify-content-center"
                        style={CHAT_FALLBACK_STYLE}
                        aria-hidden
                      >
                        <span className="text-muted">{t("messages.loading")}</span>
                      </div>
                    }
                  >
                  <ChatWindow
                    users={userResponse}
                    handleUserClick={handleUserClick}
                    setSelectedUser={setSelectedUser}
                    setShowProfileModal={setShowProfileModal}
                    showProfileModal={showProfileModal}
                    selectedUser={selectedUser}
                    userId={userId}
                    conversation={selected}
                    connection={hubConnection}
                    isTyping={!!typingUsers[selected.id]}
                    isPeerOnline={onlineUserObjectIds.has(selected.objectId)}
                    outboundMessageStatus={outboundMessageStatus}
                    translationEnabled={translationEnabled}
                    targetLanguage={targetLanguage}
                    messagingEnabled={composeEnabled}
                    explorerMessagingLimitReached={explorerMessagingLimitReached}
                    selectedConnectionAllowed={selectedConnectionAllowed}
                    onExplorerMessagingLimitRefetch={refetchExplorerMessagingLimit}
                    onMessagingBlocked={refetchConnections}
                    onShowMessagingPaywall={() => openMessagingPaywall({ toast: false })}
                    onMessagingLimitError={handleMessagingLimitError}
                  />
                  </Suspense>
                ) : (
                  <div className="d-flex justify-content-center align-items-center h-100 text-muted p-4">
                    {t("messages.selectConversation")}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="d-flex justify-content-center align-items-center h-100 text-muted p-4">
              {t("messages.connectToStart")}
            </div>
          )}
        </div>
      </div>

      <Modal
        show={mobileChatOpen}
        onHide={handleCloseMobileChat}
        fullscreen
        backdrop="static"
        className="d-md-none"
      >
        <Modal.Header className="border-bottom d-flex align-items-center justify-content-between px-3 py-2">
          <div className="d-flex align-items-center flex-grow-1 min-w-0">
            <Button
              variant="link"
              className="text-decoration-none text-dark p-0 me-2 flex-shrink-0"
              onClick={handleCloseMobileChat}
            >
              <img
              src="/back.png"
              alt={t("messages.backAria")}
              width={BACK_ICON_SIZE}
              height={BACK_ICON_SIZE}
              loading="lazy"
              decoding="async"
              fetchPriority="low"
            />
            </Button>

            <div className="min-w-0" onClick={handleModalHeaderNameClick}>
              <div
                className="fw-medium"
                style={MODAL_HEADER_SPAN_STYLE}
                title={
                  translatedSelectedName?.fullName ||
                  `${selected?.firstName || ""} ${selected?.lastName || ""}`.trim()
                }
              >
                {translatedSelectedName?.fullName ||
                  `${selected?.firstName || ""} ${selected?.lastName || ""}`.trim()}
              </div>
              {selected && typingUsers[selected.id] ? (
                <small className="text-primary d-block">
                  {t("messages.chat.typing", { name: selected.firstName ?? "" })}
                </small>
              ) : selected && onlineUserObjectIds.has(selected.objectId) ? (
                <small className="text-success d-block">
                  {t("messages.chat.online")}
                </small>
              ) : null}
            </div>
          </div>
          {user?.user?.isAdmin === 1 && (
            <Button
              variant="link"
              className="text-decoration-none text-dark p-0 flex-shrink-0"
              onClick={handleVideoCallClick}
            >
              <FaVideo size={20} />
            </Button>
          )}
        </Modal.Header>
        <Modal.Body className="p-0">
          {selected && (
            <Suspense
              fallback={
                <div
                  className="d-flex align-items-center justify-content-center"
                  style={CHAT_FALLBACK_STYLE}
                  aria-hidden
                >
                  <span className="text-muted">{t("messages.loading")}</span>
                </div>
              }
            >
              <ChatWindow
                users={userResponse}
                userId={userId}
                conversation={selected}
                connection={hubConnection}
                isTyping={!!typingUsers[selected.id]}
                isPeerOnline={onlineUserObjectIds.has(selected.objectId)}
                outboundMessageStatus={outboundMessageStatus}
                translationEnabled={translationEnabled}
                targetLanguage={targetLanguage}
                messagingEnabled={composeEnabled}
                explorerMessagingLimitReached={explorerMessagingLimitReached}
                selectedConnectionAllowed={selectedConnectionAllowed}
                onExplorerMessagingLimitRefetch={refetchExplorerMessagingLimit}
                onMessagingBlocked={refetchConnections}
                onShowMessagingPaywall={() => openMessagingPaywall({ toast: false })}
                onMessagingLimitError={handleMessagingLimitError}
              />
            </Suspense>
          )}
        </Modal.Body>
      </Modal>
      {explorerMessagingLimitModal}
    </main>
  );
}

MessagesInner.displayName = "Messages";

export default memo(MessagesInner);
